import React from 'react'

import { Box, Color, Display, FlexDirection, ZIndex } from '@gousto-internal/citrus-react'
import { useSelector } from 'react-redux'
import useSWR from 'swr'

import { menuFetcher } from '@library/api-menu-experience'
import type { MenuQueryInput } from '@library/api-menu-experience'
import { NavBar } from '@library/menu-page'

import Loading from 'routes/Menu/Loading'
import { getBasketDate, getNumPortions } from 'selectors/basket'

import { BrowseScreenWrapper } from './BrowseScreenWrapper'
import { CollectionScreenWrapper } from './CollectionScreenWrapper'

type MenuExperiencePageProps = {
  location: {
    query: {
      collection?: string
    }
  }
  [key: string]: unknown
}

type MenuExperienceSWRKey = {
  key: 'menu-experience-menu'
  input: MenuQueryInput
}

const fetchMenuExperience = ({ input }: MenuExperienceSWRKey) => menuFetcher(input)

export const MenuExperiencePage = ({ location }: MenuExperiencePageProps) => {
  const deliveryDate = useSelector(getBasketDate)
  const numPortions = useSelector(getNumPortions)
  const categorySlug = location.query.collection

  const { data, error } = useSWR((): MenuExperienceSWRKey | null => {
    if (!deliveryDate || !numPortions) {
      return null
    }

    return {
      key: 'menu-experience-menu',
      input: {
        deliveryDate,
        numPortions,
        ...(categorySlug && { categorySlug }),
      },
    }
  }, fetchMenuExperience)

  const hasInitialLoadError = !data && error

  if (hasInitialLoadError) {
    throw error
  }

  if (!data) {
    return (
      <Box data-testid="loading">
        <Loading loading />
      </Box>
    )
  }

  const links = data.categories.map((category) => ({
    text: category.title,
    slug: category.slug,
    selected: category.slug === data.categorySlug,
    onClick: () => {},
  }))

  const ScreenWrapper = data.type === 'browse' ? BrowseScreenWrapper : CollectionScreenWrapper

  return (
    <Box data-testid="menu-experience-page">
      {links.length > 0 && (
        <Box bg={Color.White} zIndex={ZIndex.Sticky} style={{ position: 'sticky', top: 0 }}>
          <NavBar links={links} />
        </Box>
      )}
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        maxWidth="1600px"
        paddingH={[3, 3, 0]}
        paddingV={4}
        style={{ margin: '0 auto' }}
      >
        <ScreenWrapper contentBlocks={data.content} />
      </Box>
    </Box>
  )
}
